import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Link, useNavigate, useParams } from "react-router";
import { Trash2 } from "lucide-react";
import { useToast } from "../../../components/ui/Toast";
import { TierEditor, makeTierKey, validateTiers } from "./TierEditor";
import type { TierDraft } from "./TierEditor";
import {
  GET_BOOKING_TYPE,
  ALL_PRICING_PLANS,
  UPDATE_BOOKING_TYPE,
  DELETE_BOOKING_TYPE,
  ALL_BOOKING_TYPES,
} from "./queries";
import type { BookingTypeRecord, PricingPlanRecord } from "./types";
import { STATE_OPTIONS, CUSTOMER_COUNT_TYPE_OPTIONS } from "./types";
import {
  ALL_AGREEMENT_TYPES,
  type AgreementTypeRecord,
} from "../../../apollo/agreementTypes";

export function BookingTypeEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const btQuery = useQuery<{ BookingType: BookingTypeRecord }>(
    GET_BOOKING_TYPE,
    { variables: { id }, skip: !id },
  );
  const ppQuery = useQuery<{ allPricingPlans: PricingPlanRecord[] }>(
    ALL_PRICING_PLANS,
    { fetchPolicy: "cache-and-network" },
  );

  // Load AgreementTypes for the document picker
  const atQuery = useQuery<{ allAgreementTypes: AgreementTypeRecord[] }>(
    ALL_AGREEMENT_TYPES,
    {
      // No status filter in the EDIT form: a service may be mapped to a type
      // that was since retired, and it must stay resolvable (so dual-write keeps
      // the correct typeKey and the service is still editable/renameable).
      variables: { page: 0, perPage: 1000, sortField: "sortOrder", sortOrder: "ASC" },
      fetchPolicy: "cache-and-network",
    },
  );
  const agreementTypes = atQuery.data?.allAgreementTypes ?? [];

  const [updateBookingType, { loading: saving }] =
    useMutation(UPDATE_BOOKING_TYPE);
  const [deleteBookingType, { loading: deleting }] =
    useMutation(DELETE_BOOKING_TYPE);

  const bt = btQuery.data?.BookingType;
  const plans = ppQuery.data?.allPricingPlans ?? [];

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [slug, setSlug] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState<number>(1);
  const [state, setState] = useState("");
  const [customerCountType, setCustomerCountType] = useState("FIXED");
  const [minCustomers, setMinCustomers] = useState(1);
  const [maxCustomers, setMaxCustomers] = useState(1);
  const [agreementTypeId, setAgreementTypeId] = useState("");
  const [tiers, setTiers] = useState<TierDraft[]>([]);
  // Track which record's data is currently loaded into the form. When the
  // route param changes (clicking a different service in the rail), this
  // mismatches and the effect re-populates from the new record.
  const [loadedId, setLoadedId] = useState<string | null>(null);

  useEffect(() => {
    if (bt && bt.id !== loadedId) {
      setName(bt.name);
      setDescription(bt.description ?? "");
      setSlug(bt.slug ?? "");
      setIsActive(bt.isActive);
      setSortOrder(bt.sortOrder ?? 1);
      setState(bt.state ?? "");
      setCustomerCountType(bt.customerCountType);
      setMinCustomers(bt.minCustomers);
      setMaxCustomers(bt.maxCustomers);
      setAgreementTypeId(bt.agreementTypeId ?? "");
      setTiers(
        bt.pricingPlans.map((t) => ({
          _key: makeTierKey(),
          pricingPlanId: t.pricingPlanId,
          minCustomerCount: t.minCustomerCount,
          maxCustomerCount: t.maxCustomerCount,
        })),
      );
      setLoadedId(bt.id);
    }
  }, [bt, loadedId]);

  // Find selected AgreementType for dual-write
  const selectedType = useMemo(
    () => agreementTypes.find((t) => t.id === agreementTypeId),
    [agreementTypes, agreementTypeId],
  );

  const tierValidation = useMemo(
    () => validateTiers(tiers, minCustomers, maxCustomers),
    [tiers, minCustomers, maxCustomers],
  );

  const canSave =
    name.trim().length > 0 &&
    // A service with no document hard-fails the booking funnel (no agreement to
    // sign) — the failure class #444 is about. Require a document explicitly.
    agreementTypeId.length > 0 &&
    // NOTE: we deliberately do NOT require selectedType here. If a service's
    // mapped type was hard-deleted (or its id is stale), the form must still be
    // editable for unrelated changes; the save payload falls back to the loaded
    // record's agreementType string, so the legacy string is never blanked.
    !saving &&
    (tiers.length === 0 || tierValidation.valid);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave || !id) return;

    try {
      await updateBookingType({
        variables: {
          id,
          name: name.trim(),
          description: description.trim() || null,
          slug: slug.trim() || null,
          isActive,
          sortOrder,
          state: state || null,
          customerCountType,
          minCustomers,
          maxCustomers,
          // Dual-write: agreementTypeId + agreementType (legacy string = typeKey).
          // Fall back to the loaded record's existing string so a resolved-but-
          // absent selectedType can never blank it (defensive; canSave also
          // requires selectedType != null).
          agreementTypeId,
          agreementType: selectedType?.typeKey ?? bt?.agreementType ?? "",
          pricingPlans: tiers.map((t) => ({
            pricingPlanId: t.pricingPlanId,
            minCustomerCount: t.minCustomerCount,
            maxCustomerCount: t.maxCustomerCount,
          })),
        },
        refetchQueries: [{ query: ALL_BOOKING_TYPES }],
      });
      toast.push({
        kind: "success",
        title: "Service updated",
        description: `${name.trim()} has been saved.`,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Unknown error";
      toast.push({ kind: "error", title: "Save failed", description: msg });
    }
  }

  async function handleDelete() {
    if (!id) return;
    const confirmed = window.confirm(
      `Delete "${name}"? This cannot be undone.`,
    );
    if (!confirmed) return;

    try {
      await deleteBookingType({
        variables: { id },
        refetchQueries: [{ query: ALL_BOOKING_TYPES }],
      });
      toast.push({
        kind: "success",
        title: "Service deleted",
        description: `${name} has been removed.`,
      });
      navigate("/settings/catalog");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Unknown error";
      toast.push({
        kind: "error",
        title: "Delete failed",
        description: msg,
      });
    }
  }

  if (btQuery.loading && !btQuery.data) {
    return (
      <div className="max-w-2xl px-7 py-8 space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-10 bg-canvas-2 rounded pulse-soft" />
        ))}
      </div>
    );
  }

  if (btQuery.error) {
    return (
      <div className="max-w-2xl px-7 py-8">
        <div className="text-rust">
          Error loading service: {btQuery.error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <form onSubmit={handleSave} className="px-7 py-8 space-y-6">
        {/* Pane header: name + delete */}
        <div className="flex items-start justify-between gap-4 mb-2">
          <div className="min-w-0">
            <div className="text-[11.5px] text-tide font-medium uppercase tracking-wider mb-1">
              Service
            </div>
            <h2 className="font-display text-[26px] font-medium tracking-tight truncate">
              {bt?.name ?? "Edit service"}
            </h2>
          </div>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="text-[11.5px] text-rust hover:text-rust/80 flex items-center gap-1 mt-2 flex-shrink-0"
          >
            <Trash2 size={12} strokeWidth={2} />
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </div>
        {/* Basic info */}
        <div>
          <div className="text-[11.5px] text-tide font-medium uppercase tracking-wider mb-3">
            Service details
          </div>
          <div className="bg-paper-hi rounded-lg shadow-card p-5 space-y-4">
            <label className="block">
              <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                Name *
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
              />
            </label>

            <label className="block">
              <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                Description
              </span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40 resize-y"
              />
            </label>

            <label className="block">
              <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                Slug
              </span>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] font-mono focus:outline-none focus:ring-2 focus:ring-tide/40"
              />
              <span className="text-[10.5px] text-ink-5 mt-0.5 block">
                Unique identifier used in the public booking URL.
              </span>
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                  State
                </span>
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
                >
                  {STATE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                  Document *
                </span>
                <select
                  value={agreementTypeId}
                  onChange={(e) => setAgreementTypeId(e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
                >
                  <option value="">Select agreement type...</option>
                  {agreementTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.isActive ? "" : " (retired)"}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <label className="block">
                <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                  Sort order
                </span>
                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) =>
                    setSortOrder(parseInt(e.target.value, 10) || 1)
                  }
                  min={1}
                  className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] num focus:outline-none focus:ring-2 focus:ring-tide/40"
                />
              </label>

              <label className="flex items-center gap-2 mt-5">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-4 h-4 rounded border-ink-5 text-tide focus:ring-tide/40"
                />
                <span className="text-[12.5px]">Active</span>
              </label>
            </div>
          </div>
        </div>

        {/* Customer configuration */}
        <div>
          <div className="text-[11.5px] text-tide font-medium uppercase tracking-wider mb-3">
            Customer configuration
          </div>
          <div className="bg-paper-hi rounded-lg shadow-card p-5 space-y-4">
            <label className="block">
              <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                Customer count type
              </span>
              <select
                value={customerCountType}
                onChange={(e) => setCustomerCountType(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
              >
                {CUSTOMER_COUNT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                  Min customers
                </span>
                <input
                  type="number"
                  value={minCustomers}
                  onChange={(e) =>
                    setMinCustomers(parseInt(e.target.value, 10) || 1)
                  }
                  min={1}
                  max={10}
                  className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] num focus:outline-none focus:ring-2 focus:ring-tide/40"
                />
              </label>
              <label className="block">
                <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                  Max customers
                </span>
                <input
                  type="number"
                  value={maxCustomers}
                  onChange={(e) =>
                    setMaxCustomers(parseInt(e.target.value, 10) || 1)
                  }
                  min={1}
                  max={10}
                  className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] num focus:outline-none focus:ring-2 focus:ring-tide/40"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Pricing tiers */}
        <div>
          <div className="text-[11.5px] text-tide font-medium uppercase tracking-wider mb-3">
            Pricing tiers
          </div>
          <div className="bg-paper-hi rounded-lg shadow-card p-5">
            <TierEditor
              tiers={tiers}
              onChange={setTiers}
              plans={plans}
              minCustomers={minCustomers}
              maxCustomers={maxCustomers}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="hair-t border-t pt-5 flex items-center justify-end gap-2">
          <Link
            to="/settings/catalog"
            className="px-3 py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3 hover:bg-paper-2"
          >
            Discard
          </Link>
          <button
            type="submit"
            disabled={!canSave}
            className="px-3.5 py-1.5 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium hover:bg-tide-d disabled:bg-ink-6 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
